import { useId, useState, type FormEvent, type JSX } from "react";
import { toast } from "sonner";
import { newId, localDateInZone, addCalendarDays, type TaskKind } from "@recipevault/shared";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";
import { Label } from "@/ui/label.js";
import { friendlyError } from "../../shared/http.js";
import { useHouseholdTimezone } from "../auth/useHouseholdTimezone.js";
import { editWeekTasks } from "./editWeek.js";

const TASK_KINDS: TaskKind[] = ["defrost", "marinate", "soak", "buy", "prep", "startBy", "other"];
const KIND_LABELS: Record<TaskKind, string> = {
  defrost: "Defrost",
  marinate: "Marinate",
  soak: "Soak",
  buy: "Buy",
  prep: "Prep",
  startBy: "Start by",
  other: "Other",
};

export interface TaskFormProps {
  weekStart: string;
}

/**
 * Prep-task fast path (spec's Slice A task list, G4).
 *
 * One line ("What to do" + "When", default today) adds a `Task` via `editWeekTasks` —
 * which lazy-creates the week exactly like G3's slot editors. An optional expander adds
 * `kind`, `startByTime`, and a "for this meal / date" lead-time control: pick a target
 * `forDate` + a number of lead days, and `doOnDate` is back-calculated client-side
 * (`forDate − leadDays`) so the user never does calendar math themselves.
 */
export function TaskForm({ weekStart }: TaskFormProps): JSX.Element {
  const tz = useHouseholdTimezone();
  const today = localDateInZone(tz, new Date());

  // Namespace every field id per mounted instance — PlanWeek renders TWO TaskForms at once
  // (the day list + the week-level list under "This week"), and static ids would collide,
  // producing invalid duplicate DOM ids and ambiguous <Label htmlFor> associations.
  const uid = useId();

  const [title, setTitle] = useState("");
  const [doOnDate, setDoOnDate] = useState(today);
  const [expanded, setExpanded] = useState(false);
  const [kind, setKind] = useState<TaskKind | undefined>(undefined);
  const [startByTime, setStartByTime] = useState("");
  const [forDate, setForDate] = useState("");
  const [leadDays, setLeadDays] = useState("");
  const [busy, setBusy] = useState(false);

  // Back-calculate doOnDate whenever both forDate and a valid lead-days count are present —
  // this is the whole point of the control: the user picks WHEN they need it for, and how
  // many days of lead time it needs, and the app does the subtraction.
  function applyLeadTime(nextForDate: string, nextLeadDays: string): void {
    setForDate(nextForDate);
    setLeadDays(nextLeadDays);
    const n = Number.parseInt(nextLeadDays, 10);
    if (nextForDate.length > 0 && Number.isFinite(n) && n >= 0) {
      setDoOnDate(addCalendarDays(nextForDate, -n));
    }
  }

  function resetForm(): void {
    setTitle("");
    setDoOnDate(today);
    setExpanded(false);
    setKind(undefined);
    setStartByTime("");
    setForDate("");
    setLeadDays("");
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (trimmedTitle.length === 0) return;
    setBusy(true);
    try {
      await editWeekTasks(weekStart, (tasks) => [
        ...tasks,
        {
          id: newId("task"),
          doOnDate,
          title: trimmedTitle,
          done: false,
          ...(kind !== undefined ? { kind } : {}),
          ...(startByTime.length > 0 ? { startByTime } : {}),
          ...(forDate.length > 0 ? { forDate } : {}),
        },
      ]);
      resetForm();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(e);
      }}
      className="flex flex-col gap-2"
    >
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex min-w-[10rem] flex-1 flex-col gap-1">
          <Label htmlFor={`${uid}-title`}>What to do</Label>
          <Input
            id={`${uid}-title`}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
            }}
            placeholder="e.g. Defrost chicken"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${uid}-when`}>When</Label>
          <Input
            id={`${uid}-when`}
            type="date"
            value={doOnDate}
            onChange={(e) => {
              setDoOnDate(e.target.value);
            }}
          />
        </div>
        <Button type="submit" disabled={busy || title.trim().length === 0}>
          Add
        </Button>
      </div>

      <button
        type="button"
        className="w-fit text-xs text-zinc-500 hover:text-zinc-300"
        onClick={() => {
          setExpanded((v) => !v);
        }}
      >
        {expanded ? "Fewer options" : "More options"}
      </button>

      {expanded && (
        <div className="flex flex-col gap-3 rounded-lg border border-zinc-800 p-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-wide text-zinc-500">Kind</span>
            <div className="flex flex-wrap gap-1.5">
              {TASK_KINDS.map((k) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={kind === k}
                  onClick={() => {
                    setKind((cur) => (cur === k ? undefined : k));
                  }}
                  className={`rounded-full border px-2.5 py-1 text-xs ${
                    kind === k
                      ? "border-emerald-500 bg-emerald-500/10 text-emerald-300"
                      : "border-zinc-700 text-zinc-400 hover:text-zinc-200"
                  }`}
                >
                  {KIND_LABELS[k]}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor={`${uid}-startby`}>Start by (time)</Label>
            <Input
              id={`${uid}-startby`}
              type="time"
              value={startByTime}
              onChange={(e) => {
                setStartByTime(e.target.value);
              }}
            />
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor={`${uid}-fordate`}>For this meal / date</Label>
              <Input
                id={`${uid}-fordate`}
                type="date"
                value={forDate}
                onChange={(e) => {
                  applyLeadTime(e.target.value, leadDays);
                }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor={`${uid}-leaddays`}>Lead days</Label>
              <Input
                id={`${uid}-leaddays`}
                type="number"
                min={0}
                value={leadDays}
                onChange={(e) => {
                  applyLeadTime(forDate, e.target.value);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
